import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { cloneDeep } from "lodash";
import { InputSwitch } from "primereact/inputswitch";
import { ProgressSpinner } from "primereact/progressspinner";
import { SelectButton } from "primereact/selectbutton";
import { Tag } from "primereact/tag";
import * as client from "../../../client";
import { useReport, useUpdateReport } from "../data";
import { policyApplicationKeys } from "../queryKeys";
import { usePolicyApplicationForms, useUpdateApplicationForm } from "./data";
import styles from "./FormSelectionTab.module.css";

type TriaSelection = NonNullable<
  client.PolicyApplicationFormsResponse["tria_selection"]
>;

const GROUP_ORDER: client.FormGroup[] = [
  "mandatory",
  "on_by_default",
  "off_by_default",
  "tria",
];

const GROUP_LABELS: Record<client.FormGroup, string> = {
  mandatory: "Mandatory",
  on_by_default: "On by Default",
  off_by_default: "Off by Default",
  tria: "TRIA",
};

const GROUP_HINTS: Partial<Record<client.FormGroup, string>> = {
  mandatory: "Always attached",
  tria: "Follows the TRIA election",
};

const TRIA_OPTIONS: { label: string; value: TriaSelection }[] = [
  { label: "Accepted", value: "Accepted" },
  { label: "Rejected", value: "Rejected" },
];

// A form an underwriter changed from its catalog default.
const isEdited = (form: client.FormSelection) =>
  !form.locked && form.enabled !== form.default_enabled;

interface FormSelectionTabProps {
  applicationId: string;
  reportId: string;
}

export const FormSelectionTab: React.FC<FormSelectionTabProps> = ({
  applicationId,
  reportId,
}) => {
  const queryClient = useQueryClient();
  const { data, isLoading } = usePolicyApplicationForms(applicationId);
  const updateForm = useUpdateApplicationForm(applicationId);
  // TRIA is a report-level term (policy_info.tria_selection), so it's edited
  // through the standard report update API rather than an application endpoint.
  const { data: report } = useReport(reportId);
  const updateReport = useUpdateReport(reportId);

  const policyInfo = report?.report_json.policy_info;

  const setTriaSelection = (selection: TriaSelection | null) => {
    if (!policyInfo) return;
    const next = cloneDeep(policyInfo);
    next.tria_selection = selection;
    updateReport.mutate(
      { policyInfo: next },
      {
        // The locked TRIA rows are recomputed from tria_selection at read time.
        onSuccess: () =>
          queryClient.invalidateQueries({
            queryKey: policyApplicationKeys.forms(applicationId),
          }),
      }
    );
  };

  if (isLoading || !data) {
    return (
      <div className={styles.loading}>
        <ProgressSpinner style={{ width: 44, height: 44 }} />
      </div>
    );
  }

  if (!data.policy_type) {
    return (
      <div className={styles.placeholder}>
        <i className="pi pi-file-edit" />
        <h3>No policy type selected</h3>
        <p>
          Choose a policy type (ISO, Co-Participation, or Manuscript) on the
          report&apos;s General tab — or as a layer override — to load its form
          set.
        </p>
      </div>
    );
  }

  const forms = data.forms;
  const enabledCount = forms.filter((form) => form.enabled).length;
  const editedCount = forms.filter(isEdited).length;
  const hasTria = forms.some((form) => form.group === "tria");

  const renderForm = (form: client.FormSelection) => {
    const edited = isEdited(form);
    return (
      <div
        key={form.form_id}
        className={`${styles.row} ${edited ? styles.editedRow : ""} ${
          !form.enabled ? styles.offRow : ""
        }`}
      >
        <span className={styles.formNumber}>{form.form_number || "—"}</span>
        <span className={styles.formName}>
          {form.form_name}
          {form.note && <span className={styles.note}>{form.note}</span>}
        </span>
        <div className={styles.rowStatus}>
          {form.locked ? (
            <>
              <span
                className={`${styles.lockedState} ${
                  form.enabled ? styles.lockedOn : styles.lockedOff
                }`}
              >
                {form.enabled ? "On" : "Off"}
              </span>
              <i
                className={`pi pi-lock ${styles.lockIcon}`}
                title={
                  form.group === "tria"
                    ? "Set by the TRIA election"
                    : "Mandatory — cannot be changed"
                }
              />
            </>
          ) : (
            <InputSwitch
              checked={form.enabled}
              onChange={(e) =>
                updateForm.mutate({
                  formId: form.form_id,
                  enabled: !!e.value,
                })
              }
            />
          )}
        </div>
        {edited && (
          <Tag
            value={form.enabled ? "Added" : "Removed"}
            severity="warning"
            className={styles.rowBadge}
          />
        )}
      </div>
    );
  };

  return (
    <div className={styles.inner}>
      <div className={styles.toolbar}>
        <div className={styles.summary}>
          <span className={styles.policyType}>{data.policy_type}</span>
          <span className={styles.counts}>
            {enabledCount} of {forms.length} forms on
            {editedCount > 0 && (
              <span className={styles.editedCount}>
                {" "}
                · {editedCount} edited
              </span>
            )}
          </span>
        </div>
        {hasTria && (
          <div className={styles.tria}>
            <span className={styles.triaLabel}>TRIA</span>
            <SelectButton
              className={styles.triaButtons}
              options={TRIA_OPTIONS}
              value={data.tria_selection}
              disabled={!policyInfo || updateReport.isPending}
              onChange={(e) =>
                setTriaSelection((e.value as TriaSelection | null) ?? null)
              }
            />
          </div>
        )}
      </div>

      {GROUP_ORDER.map((group) => {
        const groupForms = forms.filter((form) => form.group === group);
        if (groupForms.length === 0) return null;
        const groupOn = groupForms.filter((form) => form.enabled).length;
        return (
          <section key={group} className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <span>{GROUP_LABELS[group]}</span>
              {GROUP_HINTS[group] && (
                <span className={styles.sectionHint}>
                  <i className="pi pi-lock" /> {GROUP_HINTS[group]}
                </span>
              )}
              <span className={styles.sectionCount}>
                {groupOn}/{groupForms.length}
              </span>
            </h2>
            <div className={styles.rows}>{groupForms.map(renderForm)}</div>
          </section>
        );
      })}
    </div>
  );
};
